import React, { useState, useMemo, useCallback } from 'react';
import { compose } from 'redux';
import { injectIntl } from '../../util/reactIntl';

import css from './CustomerAboutMeContainer.css';

// Constants
const BIO_PREVIEW_LENGTH = 600;

// Helper function to safely slice bio without cutting HTML entities
const getSafeBioSlice = (text, maxLength) => {
  if (text.length <= maxLength) return text;

  let sliced = text.slice(0, maxLength);

  // Check if we're cutting off in the middle of an HTML entity
  const lastAmpersand = sliced.lastIndexOf('&');
  const lastSemicolon = sliced.lastIndexOf(';');

  // If there's an ampersand after the last semicolon, we might be cutting an entity
  if (lastAmpersand > lastSemicolon) {
    // Find the next semicolon in the original text after our cut point
    const nextSemicolon = text.indexOf(';', maxLength);
    if (nextSemicolon !== -1 && nextSemicolon - lastAmpersand < 10) {
      // It's likely an HTML entity, so cut before the ampersand
      sliced = text.slice(0, lastAmpersand);
    }
  }

  return sliced;
};

// Custom hook for profile data
const useProfileData = profileUser => {
  return useMemo(() => {
    const bio = (profileUser?.attributes?.profile?.publicData?.bio || '').replace(/&nbsp;/g, ' ');
    return { bio };
  }, [profileUser]);
};

// Custom hook for translation messages
const useTranslationMessages = intl => {
  return useMemo(
    () => ({
      header: intl.formatMessage({ id: 'FreelancerAboutMeContainer.header' }),
      showMore: intl.formatMessage({ id: 'FreelancerAboutMeContainer.showMore' }),
      showLess: intl.formatMessage({ id: 'FreelancerAboutMeContainer.showLess' }),
    }),
    [intl]
  );
};

const CustomerAboutMeContainerComponent = ({ profileUser, intl }) => {
  const [showFullBio, setShowFullBio] = useState(false);

  const { bio } = useProfileData(profileUser);
  const messages = useTranslationMessages(intl);

  // Memoized bio data
  const bioData = useMemo(() => {
    const isBioLong = bio.length > BIO_PREVIEW_LENGTH;
    const displayedBio =
      showFullBio || !isBioLong ? bio : `${getSafeBioSlice(bio, BIO_PREVIEW_LENGTH)}...`;
    return { isBioLong, displayedBio };
  }, [bio, showFullBio]);

  // Event handler
  const toggleBio = useCallback(() => setShowFullBio(prev => !prev), []);

  return (
    <div className={css.aboutMeContainer}>
      <div className={css.sectionTitle}>{messages.header}</div>
      <div className={css.bio}>
        <div dangerouslySetInnerHTML={{ __html: bioData.displayedBio }} />
        {bioData.isBioLong && (
          <div className={css.toggleShowMore} onClick={toggleBio}>
            {showFullBio ? messages.showLess : messages.showMore}
          </div>
        )}
      </div>
    </div>
  );
};

const CustomerAboutMeContainer = compose(injectIntl)(CustomerAboutMeContainerComponent);

export default CustomerAboutMeContainer;
